import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Calendar,
  Building2,
  Activity as ActivityIcon,
  ClipboardList,
  DollarSign,
  LogOut,
  History,
  TrendingUp,
  TrendingDown,
  Users,
  Mail,
  Globe,
  Compass,
  Shield,
  Handshake
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Role-based menu items
  const getMenuItems = () => {
    const role = user?.role || 'volunteer';
    
    // Admin: Full access
    if (role === 'admin') {
      return [
        { path: '/dashboard', label: 'Global Dashboard', icon: LayoutDashboard },
        { path: '/establishment-stats', label: 'Establishment Stats', icon: TrendingUp },
        { path: '/reservations', label: 'Reservations', icon: Calendar },
        { path: '/establishments', label: 'Establishments', icon: Building2 },
        { path: '/accommodation-types', label: 'Room Types', icon: Building2 },
        { path: '/activities', label: 'Activities', icon: ActivityIcon },
        { path: '/activity-management', label: 'Activity Bookings', icon: Compass },
        { path: '/upcoming', label: 'Upcoming', icon: ClipboardList },
        { path: '/transactions', label: 'Transactions', icon: DollarSign },
        { path: '/history', label: 'History', icon: History },
        { path: '/access-management', label: 'Access', icon: Shield },
        { path: '/affiliate-management', label: 'Affiliates', icon: Handshake },
      ];
    }
    
    // Reservation Manager: Access to everything except global stats, access mgmt, establishment settings
    if (role === 'reservation_manager') {
      return [
        { path: '/establishment-stats', label: 'Establishment Stats', icon: TrendingUp },
        { path: '/reservations', label: 'Reservations', icon: Calendar },
        { path: '/accommodation-types', label: 'Room Types', icon: Building2 },
        { path: '/activities', label: 'Activities', icon: ActivityIcon },
        { path: '/activity-management', label: 'Activity Bookings', icon: Compass },
        { path: '/upcoming', label: 'Upcoming', icon: ClipboardList },
        { path: '/transactions', label: 'Transactions', icon: DollarSign },
        { path: '/history', label: 'History', icon: History },
      ];
    }
    
    // Worker: Limited to their establishment - stats, activities, expenses
    if (role === 'worker') {
      return [
        { path: '/establishment-stats', label: 'My Stats', icon: TrendingUp },
        { path: '/reservations', label: 'Reservations', icon: Calendar },
        { path: '/activities', label: 'Activities', icon: ActivityIcon },
        { path: '/upcoming', label: 'Upcoming', icon: ClipboardList },
        { path: '/transactions', label: 'Expenses', icon: DollarSign },
      ];
    }
    
    // Volunteer: Very limited - activities list + all clients
    return [
      { path: '/upcoming', label: 'Upcoming', icon: ClipboardList },
      { path: '/reservations', label: 'All Clients', icon: Users },
      { path: '/activities', label: 'Activities', icon: ActivityIcon },
    ];
  };

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg sm:text-xl font-bold text-blue-600 ml-2 lg:ml-0">Wostel Djerba</h1>
              <span className="ml-2 text-xs sm:text-sm text-gray-500 hidden sm:inline">Management</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm text-gray-600 truncate max-w-[120px] sm:max-w-none">{user?.email}</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                {user?.role}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex flex-col lg:flex-row">
        <aside className={`w-full lg:w-64 bg-white lg:min-h-screen shadow-sm ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`}>
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'volunteer') {
      navigate('/upcoming', { replace: true });
      return;
    }
    // Worker: auto-select their establishment
    if (user?.role === 'worker' && user?.establishment_id) {
      setSelectedEstablishment(user.establishment_id);
    }
    loadEstablishments();
  }, [user, navigate]);

  const loadEstablishments = async () => {
    try {
      const res = await axios.get(`${API}/establishments`);
      setEstablishments(res.data);
    } catch (error) {
      console.error('Failed to load establishments');
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const url = selectedEstablishment
        ? `${API}/dashboard/stats/${selectedEstablishment}`
        : `${API}/dashboard/stats`;
      const res = await axios.get(url);
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [selectedEstablishment]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const sendTodaysPassports = async () => {
    try {
      await axios.post(`${API}/send-todays-passports`);
      toast.success('Passports sent to email!');
    } catch (error) {
      toast.error('Failed to send passports');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{user?.role === 'worker' ? `Stats - ${user?.establishment_name || 'My Establishment'}` : 'Establishment Stats'}</h2>
            {user?.role !== 'worker' && (
              <select
                value={selectedEstablishment}
                onChange={(e) => { setSelectedEstablishment(e.target.value); setLoading(true); }}
                className="mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="dashboard-establishment-filter"
              >
                <option value="">All Establishments</option>
                {establishments.map((est) => (
                  <option key={est.id} value={est.id}>{est.name}</option>
                ))}
              </select>
            )}
          </div>
          {user?.role !== 'worker' && (
            <button
              onClick={sendTodaysPassports}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              <Mail className="w-4 h-4" />
              <span>Send Today&apos;s Passports</span>
            </button>
          )}
        </div>

        {/* Financial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Total Gain</h3>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total_gain_dt.toFixed(2)} DT</p>
            <div className="mt-3 text-xs text-gray-500">
              <div>EUR: {stats.gain_breakdown.eur.toFixed(2)}</div>
              <div>USD: {stats.gain_breakdown.usd.toFixed(2)}</div>
              <div>DT: {stats.gain_breakdown.dt.toFixed(2)}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Cash</h3>
              <DollarSign className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.cash_dt.toFixed(2)} DT</p>
            <div className="mt-3 text-xs text-gray-500">
              <div>EUR: {stats.cash_breakdown.eur.toFixed(2)}</div>
              <div>USD: {stats.cash_breakdown.usd.toFixed(2)}</div>
              <div>DT: {stats.cash_breakdown.dt.toFixed(2)}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Online</h3>
              <DollarSign className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.online_dt.toFixed(2)} DT</p>
            <div className="mt-3 text-xs text-gray-500">
              <div>EUR: {stats.online_breakdown.eur.toFixed(2)}</div>
              <div>USD: {stats.online_breakdown.usd.toFixed(2)}</div>
              <div>DT: {stats.online_breakdown.dt.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* NEW: Accommodation Revenue */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm p-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-blue-900">Accommodation Revenue</h3>
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-900">{(stats.accommodation_revenue_dt || 0).toFixed(2)} DT</p>
            <div className="mt-3 text-xs text-blue-700">
              <div>EUR: {(stats.accommodation_revenue_breakdown?.eur || 0).toFixed(2)}</div>
              <div>USD: {(stats.accommodation_revenue_breakdown?.usd || 0).toFixed(2)}</div>
              <div>DT: {(stats.accommodation_revenue_breakdown?.dt || 0).toFixed(2)}</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm p-6 border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-purple-900">Activities Gain</h3>
              <ActivityIcon className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-900">{(stats.activities_gain_dt || 0).toFixed(2)} DT</p>
          </div>
        </div>

        {/* Expenses & Net Gain */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-sm p-6 border border-red-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-red-900">Total Expenses</h3>
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-900">{stats.total_expenses_dt.toFixed(2)} DT</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-6 border border-green-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-green-900">Net Gain</h3>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-900">{stats.net_gain_dt.toFixed(2)} DT</p>
          </div>
        </div>

        {/* Reservations & Checkouts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Reservations</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.total_reservations}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Checkouts Today</h3>
            <p className="text-2xl font-bold text-orange-600">{stats.checkouts_today}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Checkouts Tomorrow</h3>
            <p className="text-2xl font-bold text-blue-600">{stats.checkouts_tomorrow}</p>
          </div>
        </div>

        {/* Reservations by Type */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reservations by Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.reservations_by_type).map(([type, count]) => (
              <div key={type} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">{type}</p>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
            ))}
          </div>
        </div>

        {/* NEW: Nationalities */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <Globe className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Guest Nationalities</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(stats.nationalities || []).slice(0, 8).map((item) => (
              <div key={item.country} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">{item.country}</span>
                <span className="text-lg font-bold text-blue-600">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Expenses */}
        {stats.recent_expenses.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Expenses</h3>
            <div className="space-y-2">
              {stats.recent_expenses.map((expense) => (
                <div key={expense.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{expense.note}</p>
                    <p className="text-sm text-gray-500">{expense.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{expense.price} {expense.currency}</p>
                    <p className="text-sm text-gray-500">{expense.price_dt.toFixed(2)} DT</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
export { Layout };
